import { Injectable } from "@angular/core";
import {
    ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Route, Router, RouterStateSnapshot
} from "@angular/router";

import { MessageService } from "primeng/api";

import { UserService } from "@main/users";
import { SecurityService } from "./security.service";
import { Message } from "../../classes";
import { constructPathFromRoute } from "../../utils/routes";


@Injectable()
export class SecurityGuard implements CanActivate, CanActivateChild, CanLoad {
    private readonly _loginUrl = "/account/login";
    private readonly _homeUrl = "/app";
    private readonly _twoFactorAuthenticationUrl = "/app/profile/authentication";

    constructor(private router: Router,
                private messageService: MessageService,
                private userService: UserService,
                private securityService: SecurityService) {
    }

    async canLoad(route: Route): Promise<boolean> {
        if (this.userService.isLogged) {
            return true;
        } else {
            const queryParams = { returnUrl: location.href };
            await this.router.navigate([this._loginUrl], { queryParams });
            return false;
        }
    }

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        await this.securityService.refreshRoutes(false);

        const currentPath = state.url.split("?")[0];
        if (this.userService.isLogged &&
            this.userService.isUserRequiredSetupTwoFactor &&
            currentPath != this._twoFactorAuthenticationUrl
        ) {
            const returnUrl = route.queryParamMap.get("returnUrl")
                ?? new URL(state.url, location.origin).searchParams.get("returnUrl");
            await this.router.navigate([this._twoFactorAuthenticationUrl], {
                queryParams: returnUrl ? { returnUrl } : {}
            });
            return false;
        }
        if (!this.userService.isLogged) {
            await this.router.navigate([this._loginUrl]);
            return false;
        }

        if (Object.keys(route.children).length) {
            return true;
        }

        const path = [state.url];
        path.push(constructPathFromRoute(route));

        if (this.securityService.isPageAvailable(path)) {
            return true;
        } else {
            this.messageService.add(Message.Error("You do not have the necessary permissions to access this screen."));
            await this.router.navigate([this._homeUrl]);
            return false;
        }
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.canActivate(childRoute, state);
    }
}